import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaPhone, FaEnvelope, FaMapMarkerAlt, FaChevronRight } from 'react-icons/fa';

const Footer = () => {
  const [email, setEmail] = useState('');
  const { language, setLanguage, t } = useLanguage();

  const handleNewsletter = (e) => {
    e.preventDefault();
    // Handle newsletter signup
    console.log('Newsletter signup:', email);
    setEmail('');
  };

  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { label: t('generalMedicine'), to: '/services/general' },
      { label: t('cardiology'), to: '/services/cardiology' },
      { label: t('pediatrics'), to: '/services/pediatrics' },
      { label: t('orthopedics'), to: '/services/orthopedics' },
      { label: t('diagnosticLab'), to: '/services/lab' },
    ],
    company: [
      { label: t('aboutUs'), to: '/about' },
      { label: t('medicalTeam'), to: '/team' },
      { label: t('careers'), to: '/careers' },
      { label: t('patientStories'), to: '/testimonials' },
    ],
    resources: [
      { label: t('healthBlog'), to: '/blog' },
      { label: t('patientPortal'), to: '/portal' },
      { label: t('privacyPolicy'), to: '/privacy' },
      { label: t('insurancePartners'), to: '/insurance' },
    ],
  };

  const socialLinks = [
    { icon: <FaFacebook />, url: 'https://facebook.com/healthbridge', label: 'Facebook' },
    { icon: <FaTwitter />, url: 'https://twitter.com/healthbridge', label: 'Twitter' },
    { icon: <FaInstagram />, url: 'https://instagram.com/healthbridge', label: 'Instagram' },
    { icon: <FaLinkedin />, url: 'https://linkedin.com/company/healthbridge', label: 'LinkedIn' },
  ];

  const contactInfo = [
    { icon: <FaPhone />, text: '+91-22-4567-8901', link: 'tel:+912245678901' },
    { icon: <FaEnvelope />, text: 'appointments@healthbridge.com', link: 'mailto:appointments@healthbridge.com' },
    { icon: <FaMapMarkerAlt />, text: '45 Healthcare Boulevard, Powai, Mumbai, India', link: '#' },
  ];

  return (
    <footer className="bg-gradient-to-b from-purple-900 to-purple-800 text-gray-300">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center">
              <h1 className="text-3xl font-bold text-white">
                <span className="text-purple-300">Health</span>
                <span className="text-white">Bridge</span>
                <span className="text-purple-300">Clinic</span>
              </h1>
            </Link>
            <p className="text-gray-400 leading-relaxed">
              Providing comprehensive healthcare services with a patient-first approach. 
              Your health and wellness is our top priority.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-purple-300 transition-colors duration-300"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">{t('services')}</h3>
              <ul className="space-y-2">
                {footerLinks.services.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="flex items-center text-gray-400 hover:text-purple-300 transition-colors duration-300"
                    >
                      <FaChevronRight className="text-xs mr-2" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">{t('company')}</h3>
              <ul className="space-y-2">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="flex items-center text-gray-400 hover:text-purple-300 transition-colors duration-300"
                    >
                      <FaChevronRight className="text-xs mr-2" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-4">
              {contactInfo.map((item, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <span className="text-purple-400 mt-1">{item.icon}</span>
                  <a
                    href={item.link}
                    className="text-gray-400 hover:text-purple-300 transition-colors duration-300"
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter & Language */}
          <div className="space-y-6">
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">{t('stayUpdated')}</h3>
              <form onSubmit={handleNewsletter} className="space-y-4">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('enterEmail')}
                    className="w-full px-4 py-3 rounded-lg bg-purple-900/50 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold transition-colors duration-300"
                >
                  {t('subscribe')}
                </button>
              </form>
            </div>

            <div>
              <h3 className="text-white font-semibold text-lg mb-4">{t('language')}</h3>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-purple-900/50 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300"
              >
                <option value="en">English</option>
                <option value="hi">हिंदी</option>
                <option value="gu">ગુજરાતી</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-purple-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400">
              © {currentYear} HealthBridge Clinic. {t('allRightsReserved')}.
            </div>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-purple-300 transition-colors duration-300">
                {t('privacyPolicy')}
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-purple-300 transition-colors duration-300">
                {t('termsOfService')}
              </Link>
              <Link to="/cookies" className="text-gray-400 hover:text-purple-300 transition-colors duration-300">
                {t('cookiePolicy')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
        aria-label="Back to top"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </button>
    </footer>
  );
};

export default Footer; 